import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FaSearch } from 'react-icons/fa';
import { PersonalConnection } from '../../../../types/misc';
import { getInitials } from '../../../../utils/stringUtils';
import ApiImage from '../../../../components/misc/ApiImage';

interface ConnectionsSidebarProps {
  connections: PersonalConnection[];
  selectedConnectionId?: number;
  onSelectConnection: (connection: PersonalConnection) => void;
  isMobile?: boolean;
}

export default function ConnectionsSidebar({
  connections,
  selectedConnectionId,
  onSelectConnection,
  isMobile = false,
}: ConnectionsSidebarProps): JSX.Element {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredConnections = React.useMemo(() => {
    if (!searchQuery.trim()) return connections;

    const query = searchQuery.toLowerCase();
    return connections.filter((connection) => {
      const fullName = `${connection.firstName} ${connection.lastName}`.toLowerCase();
      const email = connection.email?.toLowerCase() || '';
      return fullName.includes(query) || email.includes(query);
    });
  }, [connections, searchQuery]);

  return (
    <div
      className={classNames('flex h-full flex-col bg-secondary', {
        'w-[360px] border-r border-details-500/50': !isMobile,
        'w-full': isMobile,
      })}>
      {/* Search bar */}
      <div className="flex flex-row items-center gap-3 border-b border-details-500/50 px-4 py-2">
        <FaSearch className="h-4 w-4 flex-shrink-0 text-primary" />
        <input
          type="text"
          placeholder={t('general.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="text-md w-full border-none bg-transparent text-primary focus:outline-none focus:ring-0"
        />
      </div>

      {/* Connections list */}
      <div className="flex-1 overflow-y-auto">
        {filteredConnections.length === 0 ? (
          <div className="flex items-center justify-center px-4 py-8">
            <p className="text-secondary-500 text-sm">
              {connections.length === 0
                ? t('page.dashboard.connections.noConnections')
                : t('page.dashboard.connections.noResults')}
            </p>
          </div>
        ) : (
          filteredConnections.map((connection, index) => {
            const fullName = `${connection.firstName} ${connection.lastName}`;
            // disable selected styling on mobile
            const isSelected = !isMobile && selectedConnectionId != null && selectedConnectionId === connection.id;
            const firstLetter = connection.firstName.charAt(0).toUpperCase();
            const previousFirstLetter =
              index > 0 ? filteredConnections[index - 1].firstName.charAt(0).toUpperCase() : '';
            const showDivider = firstLetter !== previousFirstLetter;

            return (
              <React.Fragment key={connection.id ?? `conn-${index}`}>
                {showDivider && (
                  <div className="border-b border-details-500/50 py-1">
                    <p className="text-secondary-500 px-4 pt-4 text-xs font-semibold uppercase tracking-wider">
                      {firstLetter}
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onSelectConnection(connection)}
                  className={classNames(
                    'flex w-full items-center gap-3 border-b px-4 py-3 transition-all hover:bg-secondary-200',
                    {
                      'border-l-4 border-l-primary bg-secondary-200 hover:bg-secondary-200/60': isSelected,
                    },
                  )}>
                  {connection.picture ? (
                    <ApiImage
                      src={connection.picture}
                      alt={fullName}
                      className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-secondary">
                      <span className="text-sm font-medium">{getInitials(fullName)}</span>
                    </div>
                  )}
                  <div className="flex-1 text-left">
                    <p
                      className={classNames(
                        'text-sm font-medium',
                        isSelected ? 'text-primary-700' : 'text-primary-900',
                      )}>
                      {fullName}
                    </p>
                  </div>
                </button>
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}
